import { z } from 'zod';

/**
 * Client-side mirror of the limits enforced in `functions/api/contact.ts`.
 * The Pages Function re-validates everything independently and is the real
 * gate — this schema exists to give people useful errors before they submit.
 * Keep the two in sync; the numbers are duplicated on purpose so the Worker
 * bundle stays dependency-free.
 */
export const LIMITS = {
  nameMin: 2,
  nameMax: 80,
  emailMax: 254,
  messageMin: 10,
  messageMax: 4000,
} as const;

export const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(LIMITS.nameMin, { message: `Please enter at least ${LIMITS.nameMin} characters.` })
    .max(LIMITS.nameMax, { message: `Please keep this under ${LIMITS.nameMax} characters.` }),
  email: z
    .string()
    .trim()
    .min(1, { message: 'Please enter your email address.' })
    .email({ message: 'That does not look like an email address.' })
    .max(LIMITS.emailMax, { message: 'That email address is too long.' }),
  message: z
    .string()
    .trim()
    .min(LIMITS.messageMin, { message: 'Please enter at least 10 characters.' })
    .max(LIMITS.messageMax, { message: `Please keep this under ${LIMITS.messageMax} characters.` }),
});

export type ContactFormValues = z.infer<typeof contactSchema>;

export type ContactResponse = {
  ok: boolean;
  error?: string;
  fields?: Record<string, string>;
};

export class ContactSubmitError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ContactSubmitError';
    this.status = status;
  }
}

export async function submitContactForm(values: ContactFormValues): Promise<ContactResponse> {
  const res = await fetch('/api/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(values),
  });

  let body: ContactResponse | null = null;
  try {
    body = (await res.json()) as ContactResponse;
  } catch {
    body = null;
  }

  if (!res.ok || !body?.ok) {
    const fallback =
      res.status === 429
        ? 'Too many messages from this connection. Please try again in a few minutes.'
        : 'Something went wrong sending your message. Please try again shortly.';
    throw new ContactSubmitError(body?.error ?? fallback, res.status);
  }

  return body;
}
